'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { cloneElement, useEffect } from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import NotificationList from 'ReduxMessenger/components/NotificationList';
import TourContext from 'ui-shepherd-react/contexts/TourContext';
import UIBox from 'UIComponents/layout/UIBox';
import UIFlex from 'UIComponents/layout/UIFlex';
import PickersContext from 'ContentComponents/context/PickersContext';
import SharedDragDropContext from 'DesignData/lib/SharedDragDropContext';
import withKeyboardShortcuts from 'DesignComponents/decorators/withKeyboardShortcuts';
import ContextMenu from 'DesignManagerIde/components/contextMenu/ContextMenu';
import ContextMenuItemGroup from 'DesignData/models/ContextMenuItemGroup';
import ErrorBoundary from 'DesignComponents/errors/ErrorBoundary';
import PaneSize from 'DesignData/models/PaneSize';
import FinderTabs from 'DesignManagerIde/components/tabs/FinderTabs';
import Pickers from 'DesignManagerIde/components/Pickers';
import KeyboardShortcutsListing from 'DesignManagerIde/components/KeyboardShortcutsListing';
import { SCROLL_ACTIVE_TAB_INTO_VIEW } from 'DesignData/enums/FlashKeys';
import ResizablePanes from 'DesignComponents/resizablePanes/ResizablePanes';
import Pane from 'DesignComponents/resizablePanes/Pane';
import StatusBar from 'DesignComponents/statusBar/StatusBar';
import FileManagerContext from 'DesignEditorsUI/context/FileManagerContext';
import UsageTrackerContext from 'DesignEditorsUI/context/UsageTrackerContext';
import usageTracker from 'DesignManagerIde/utils/usageTracker/usageTracker';
import FileManager from 'DesignManagerIde/components/shared/FileManager';
import DndAreasInCustomTemplatesFeedback from 'DesignManagerIde/components/feedback/DndAreasInCustomTemplatesFeedback';
import { PIXELS } from 'DesignData/enums/SizeUnits';
import { connect } from 'react-redux';
import { getUserId, getPortalId } from 'DesignData/selectors/auth';
import { MIN_EDITOR_SIZE, MIN_FINDER_SIZE } from 'DesignData/constants/PaneSizes';
import { FINDER, EDITOR } from 'DesignData/enums/ApplicationPaneTypes';
import FinderContainer from '../containers/Finder';

const Application = props => {
  useEffect(() => {
    const {
      isFinderShowing,
      alwaysShowFinderOnStartup,
      toggleFinder
    } = props;

    if (alwaysShowFinderOnStartup && !isFinderShowing) {
      toggleFinder(true);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []);

  const onFinderResize = sizes => {
    props.setFinderWidth(sizes[FINDER]);
  };

  const onFinderToggleButtonClick = () => {
    const {
      isFinderShowing,
      toggleFinder
    } = props;
    toggleFinder(!isFinderShowing);
  };

  const onEditorClick = () => {
    if (props.shouldScrollActiveTabIntoView) {
      props.flash(SCROLL_ACTIVE_TAB_INTO_VIEW, true);
    }
  };

  const renderFinderContainer = () => {
    const {
      finderComponent,
      closeContextMenu,
      openContextMenu,
      params
    } = props;
    return /*#__PURE__*/_jsx(FinderContainer, {
      closeContextMenu: closeContextMenu,
      openContextMenu: openContextMenu,
      params: params,
      children: finderComponent
    });
  };

  const renderEditorContainer = () => {
    const {
      mainComponent,
      openContextMenu,
      isFinderShowing,
      setIsKeyboardShortcutsListingShowing
    } = props;
    return /*#__PURE__*/_jsxs(UIFlex, {
      align: "stretch",
      direction: "column",
      wrap: "nowrap",
      children: [/*#__PURE__*/_jsx(FinderTabs, {
        openContextMenu: openContextMenu,
        isFinderShowing: isFinderShowing,
        onFinderToggleButtonClick: onFinderToggleButtonClick,
        setIsKeyboardShortcutsListingShowing: setIsKeyboardShortcutsListingShowing
      }), /*#__PURE__*/_jsx(ErrorBoundary, {
        boundaryName: "editor-container",
        children: /*#__PURE__*/_jsx("div", {
          className: "editor-container",
          onClick: onEditorClick,
          children: /*#__PURE__*/cloneElement(mainComponent, Object.assign({}, props))
        })
      })]
    });
  };

  const renderContextMenu = () => {
    const {
      closeContextMenu,
      isContextMenuOpen,
      contextMenuCoordX,
      contextMenuCoordY,
      rightClickCoordX,
      rightClickCoordY,
      verticalOverflow,
      setContextMenuLocation,
      contextMenuContentData,
      swapContextMenuContent
    } = props;
    return /*#__PURE__*/_jsx(ContextMenu, {
      className: "application-container__context-menu",
      closeContextMenu: closeContextMenu,
      contextMenuCoordX: contextMenuCoordX,
      contextMenuCoordY: contextMenuCoordY,
      isOpen: isContextMenuOpen,
      rightClickCoordX: rightClickCoordX,
      rightClickCoordY: rightClickCoordY,
      verticalOverflow: verticalOverflow,
      setContextMenuLocation: setContextMenuLocation,
      contextMenuContentData: contextMenuContentData,
      swapContextMenuContent: swapContextMenuContent
    });
  };

  const renderKeyboardShortcutsListing = () => {
    const {
      isKeyboardShortcutsListingShowing,
      setIsKeyboardShortcutsListingShowing
    } = props;

    if (!isKeyboardShortcutsListingShowing) {
      return null;
    }

    return /*#__PURE__*/_jsx(KeyboardShortcutsListing, {
      setIsKeyboardShortcutsListingShowing: setIsKeyboardShortcutsListingShowing
    });
  };

  const {
    isFinderShowing,
    finderWidth
  } = props;
  const defaultFinderSize = isFinderShowing ? finderWidth : new PaneSize({
    value: 0
  });
  const minFinderSize = new PaneSize({
    value: isFinderShowing ? MIN_FINDER_SIZE : 0
  });
  const minEditorSize = new PaneSize({
    value: MIN_EDITOR_SIZE
  });
  return /*#__PURE__*/_jsx(FileManagerContext.Provider, {
    value: FileManager,
    children: /*#__PURE__*/_jsx(UsageTrackerContext.Provider, {
      value: {
        tracker: usageTracker,
        appName: 'design-manager-ide'
      },
      children: /*#__PURE__*/_jsxs(PickersContext.Provider, {
        value: Pickers,
        children: [/*#__PURE__*/_jsxs(UIFlex, {
          align: "stretch",
          direction: "column",
          children: [/*#__PURE__*/_jsxs(ResizablePanes, {
            className: "application-pane",
            direction: "vertical",
            onPaneResize: onFinderResize,
            resizerClassName: "finder",
            children: [/*#__PURE__*/_jsxs(Pane, {
              unitsOnResize: PIXELS,
              defaultSize: defaultFinderSize,
              minSize: minFinderSize,
              paneId: FINDER,
              resizeDisabled: !isFinderShowing,
              scrollable: false,
              children: [/*#__PURE__*/_jsx(ErrorBoundary, {
                boundaryName: "finder-container",
                children: /*#__PURE__*/_jsx("div", {
                  className: "application-container__finder",
                  children: renderFinderContainer()
                })
              }), /*#__PURE__*/_jsx(NotificationList, {}), renderContextMenu(), renderKeyboardShortcutsListing()]
            }), /*#__PURE__*/_jsx(Pane, {
              className: "application-container__main",
              minSize: minEditorSize,
              paneId: EDITOR,
              scrollable: false,
              children: renderEditorContainer()
            })]
          }), /*#__PURE__*/_jsx(UIBox, {
            grow: 0,
            shrink: 0,
            children: /*#__PURE__*/_jsx(StatusBar, {})
          })]
        }), /*#__PURE__*/_jsx(DndAreasInCustomTemplatesFeedback, {})]
      })
    })
  });
};

Application.contextType = TourContext;
Application.propTypes = {
  mainComponent: PropTypes.node,
  finderComponent: PropTypes.node,
  closeContextMenu: PropTypes.func.isRequired,
  contextMenuCoordX: PropTypes.number.isRequired,
  contextMenuCoordY: PropTypes.number.isRequired,
  verticalOverflow: PropTypes.bool.isRequired,
  contextMenuContentData: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.instanceOf(ContextMenuItemGroup)), PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  isContextMenuOpen: PropTypes.bool.isRequired,
  isFinderShowing: PropTypes.bool.isRequired,
  alwaysShowFinderOnStartup: PropTypes.bool.isRequired,
  isKeyboardShortcutsListingShowing: PropTypes.bool.isRequired,
  openContextMenu: PropTypes.func.isRequired,
  swapContextMenuContent: PropTypes.func.isRequired,
  params: PropTypes.object.isRequired,
  rightClickCoordX: PropTypes.number.isRequired,
  rightClickCoordY: PropTypes.number.isRequired,
  setContextMenuLocation: PropTypes.func.isRequired,
  toggleFinder: PropTypes.func.isRequired,
  setFinderWidth: PropTypes.func.isRequired,
  setIsKeyboardShortcutsListingShowing: PropTypes.func.isRequired,
  shouldScrollActiveTabIntoView: PropTypes.bool.isRequired,
  flash: PropTypes.func.isRequired,
  finderWidth: PropTypes.instanceOf(PaneSize)
};
export default compose(SharedDragDropContext(), withKeyboardShortcuts(), connect(state => ({
  userId: getUserId(state),
  portalId: getPortalId(state)
}), {}))(Application);